import ConnectionImage from "@/assets/img/compatibility/connection.png"
import ConnectionIntroImage from "@/assets/img/compatibility/connection-intro.png"

import { ANALYTICS_EVENTS } from "@/features/analytics"
import { CompatibilityConfirmStep } from "../components/steps/compatibility-confirm-step"
import { CompatibilityCoverStep } from "../components/steps/compatibility-cover-step"
import { CompatibilityEmailStep } from "../components/steps/compatibility-email-step"
import { CompatibilityInterstitialStep } from "../components/steps/compatibility-interstitial-step"
import { CompatibilityProcessingStep } from "../components/steps/compatibility-processing-step"
import { ageFromDob } from "./event-params"
import type { QuizConfig, QuizOption } from "../types"

/** Male/Female/Other, reused for the user and the partner gender steps. */
const GENDER_OPTIONS: QuizOption[] = [
  { label: "Male", value: "male", icon: "mars" },
  { label: "Female", value: "female", icon: "venus" },
  { label: "Other", value: "other", icon: "ellipsis" },
]

/** Name rule (legacy LPR parity), reused for the user and partner name steps. */
const NAME_VALIDATION = {
  pattern: "^(?=.*\\p{L})[\\p{L} '’-]{3,}$",
  message: "Please enter a valid name (3+ letters, no numbers or symbols)",
}

/**
 * Live Palmistry 4.0 "Love Compatibility" quiz (Figma 3594-16122 et al.).
 * Cover → 12 questions (self + partner) → interstitial → processing → email,
 * then completion routes to the `compatibility` checkout variant.
 *
 * Named GA4 events (Trello GLvba5C9): `event`/`eventOnShow` fire from the engine;
 * the `Compat_Sub_Screen_*` events are pointwise in `compatibility-checkout.tsx`.
 * The `Loaded` params (timestamp/device_type/campaign_id/campaign_name) ride on
 * every event globally via `ga.ts`, so `eventOnShow` needs no params here.
 */
export const compatibilityQuiz: QuizConfig = {
  id: "compatibility",
  name: "Love Compatibility",
  steps: [
    {
      id: "cover",
      eventOnShow: ANALYTICS_EVENTS.COMPAT_QUIZ_LOADED,
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_START,
      type: "cover",
      title: "Reveal Your Love Compatibility",
      subtitle:
        "Take a quick quiz to discover your relationship strengths, chemistry, and soulmate potential.",
      features: [
        { icon: "clock", label: "3-minute quiz" },
        { icon: "lines", label: "Compatibility insights" },
        { icon: "compass", label: "Relationship guide" },
      ],
      cta: "Start reading",
      countsTowardProgress: false,
      component: CompatibilityCoverStep,
    },
    {
      id: "name",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_1NAME,
      type: "input",
      title: "What's your name?",
      subtitle: "We'll use this to personalize your experience",
      placeholder: "Enter your name",
      required: true,
      validation: NAME_VALIDATION,
    },
    {
      id: "dob",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_2BIRTHDAY,
      eventParams: (a) => ({ age: ageFromDob(a) }),
      type: "date",
      dateMode: "dob",
      title: "What's your date of birth?",
      subtitle:
        "We'll use this to reveal your zodiac sign, birth chart, and compatibility insights",
      required: true,
    },
    {
      id: "tob",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_3BIRTHTIME,
      eventParams: (a) => ({ select: a.tob === "unknown" ? "skipped" : "entered" }),
      type: "time",
      title: "Do you know your birth time?",
      subtitle: "This helps us calculate a more accurate compatibility reading",
      required: true,
      secondaryAction: { label: "I don't remember", value: "unknown" },
    },
    {
      id: "gender",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_4GENDER,
      eventParams: (a) => ({ gender: a.gender }),
      type: "single-choice",
      layout: "icons",
      title: "What's your gender?",
      subtitle: "This helps us personalize your compatibility insights",
      required: true,
      options: GENDER_OPTIONS,
    },
    {
      id: "relationship-feel",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_5FEEL,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      boldOptions: true,
      title: "How does this relationship feel?",
      required: true,
      options: [
        { label: "Safe & calm", value: "safe-calm" },
        { label: "Passionate", value: "passionate" },
        { label: "Complicated", value: "complicated" },
        { label: "Exciting", value: "exciting" },
        { label: "Deep", value: "deep" },
        { label: "Uncertain", value: "uncertain" },
      ],
    },
    {
      // Mid-quiz interstitial (Figma Step5.1) — between the user's own questions
      // and the partner questions. Not counted in the progress total.
      id: "connection-intro",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_EXPLORE,
      type: "info",
      title: "Now let's explore your connection together",
      subtitle: "We'll compare your energies, emotional patterns, and romantic compatibility",
      coverImage: ConnectionIntroImage,
      countsTowardProgress: false,
      component: CompatibilityInterstitialStep,
    },
    {
      id: "partner-name",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_6PARTNER_NAME,
      type: "input",
      title: "What's your partner's name?",
      subtitle: "This helps us tailor your compatibility results",
      placeholder: "Enter your partner's name",
      required: true,
      validation: NAME_VALIDATION,
    },
    {
      id: "partner-dob",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_7PARTNER_BIRTHDAY,
      eventParams: (a) => ({ age: ageFromDob(a, "partner-dob") }),
      type: "date",
      dateMode: "dob",
      title: "What's your partner's date of birth?",
      subtitle:
        "This helps us reveal the strengths, challenges, and potential of your connection",
      required: true,
    },
    {
      id: "partner-tob",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_8PARTNER_BIRTHTIME,
      eventParams: (a) => ({ select: a["partner-tob"] === "unknown" ? "skipped" : "entered" }),
      type: "time",
      title: "Do you know your partner's birth time?",
      subtitle:
        "Birth time unlocks deeper insights into your connection and long-term potential",
      required: true,
      secondaryAction: { label: "I don't know", value: "unknown" },
    },
    {
      id: "partner-gender",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_9PARTNER_GENDER,
      eventParams: (a) => ({ gender: a["partner-gender"] }),
      type: "single-choice",
      layout: "icons",
      title: "What's your partner's gender?",
      subtitle: "This helps us personalize your compatibility analysis",
      required: true,
      options: GENDER_OPTIONS,
    },
    {
      id: "discover",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_10GOAL,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      boldOptions: true,
      title: "What are you hoping to discover?",
      required: true,
      options: [
        { label: "Soulmate potential", value: "soulmate-potential" },
        { label: "Long-term compatibility", value: "long-term-compatibility" },
        { label: "Emotional connection", value: "emotional-connection" },
        { label: "Future together", value: "future-together" },
        { label: "Hidden red flags", value: "hidden-red-flags" },
      ],
    },
    {
      id: "pull-back",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_11PROBLEM,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      boldOptions: true,
      title: "What keeps pulling you back to this connection?",
      required: true,
      options: [
        { label: "Emotional bond", value: "emotional-bond" },
        { label: "Physical attraction", value: "physical-attraction" },
        { label: "Comfort", value: "comfort" },
        { label: "Curiosity", value: "curiosity" },
        { label: "Chemistry", value: "chemistry" },
        { label: "I don't know", value: "i-dont-know" },
      ],
    },
    {
      id: "interstitial",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_INSIGHT,
      type: "info",
      title: "Your connection already shows strong emotional alignment",
      subtitle:
        "We'll now analyze your connection to reveal compatibility insights and hidden emotional patterns",
      coverImage: ConnectionImage,
      countsTowardProgress: false,
      component: CompatibilityInterstitialStep,
    },
    {
      id: "analysis",
      eventOnShow: ANALYTICS_EVENTS.COMPAT_QUIZ_PROCESSING,
      type: "analysis",
      title: "Calculating your Love Compatibility...",
      countsTowardProgress: false,
      checklist: [
        "Analyzing emotional patterns...",
        "Calculating romantic alignment...",
        "Generating your compatibility reading...",
      ],
    },
    {
      id: "email",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_EMAIL,
      type: "email",
      title: "Are you ready to discover your compatibility?",
      subtitle:
        "Enter your email to get your personalized compatibility report and emotional insights.",
      placeholder: "Enter your email",
      required: true,
      legalNote: true,
      countsTowardProgress: false,
      submitDelayMs: 0,
      component: CompatibilityEmailStep,
    },
    {
      // Email opt-in confirmation (Figma LPR_Quiz_Compatibility_Confirm).
      id: "confirm",
      event: ANALYTICS_EVENTS.COMPAT_QUIZ_PRESUBSCRIPTION,
      eventParams: (a) => ({ option: a.confirm === "declined" ? "skip" : "confirm" }),
      type: "info",
      title: "Get access to your compatibility reading and exclusive updates.",
      subtitle:
        "Confirm you'd like to receive your personalized compatibility reading and future relationship insights by email.",
      secondaryAction: { label: "No, thanks", value: "declined" },
      countsTowardProgress: false,
      component: CompatibilityConfirmStep,
    },
  ],
  stepOverrides: {
    // "Calculating your Love Compatibility…" — not astrology, so override the
    // shared zodiac analysis step with a dumb compatibility processing screen.
    analysis: {
      fullBleed: true,
      validate: () => true,
      Component: CompatibilityProcessingStep,
    },
  },
}
